import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Eye } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { currentMonth, inr, monthLabel } from "../lib/format";
import { PageHeader, EmptyState, StatCard } from "../components/Common";
import { PayslipDialog } from "../components/Payslip";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Payroll() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [month, setMonth] = useState(currentMonth());
  const [list, setList] = useState([]);
  const [slip, setSlip] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ents, setEnts] = useState([]);
  const [ent, setEnt] = useState("all");

  useEffect(() => {
    if (isAdmin) api.get("/entities").then((r) => setEnts(r.data));
  }, [isAdmin]);
  const load = () => api.get("/payroll", { params: isAdmin ? { month, ...(ent !== "all" && { entity_id: ent }) } : {} }).then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, [month, ent]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/run", { month, entity_id: ent === "all" ? null : ent });
      toast.success(`Payroll processed for ${data.processed} employees`);
      load();
    } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };

  const sum = (k) => list.reduce((s, p) => s + (k === "pf" ? p.deductions.pf : p[k]), 0);

  return (
    <div data-testid="payroll-page">
      <PageHeader eyebrow="Compensation" title={isAdmin ? "Payroll" : "My payslips"}
        subtitle={isAdmin ? "Uses each entity's payroll formula (Settings) plus any per-employee breakup set on the Employees page. Unpaid leave reduces paid days." : "Your monthly salary statements."}
        actions={isAdmin && <>
          {ents.length > 1 && (
            <Select value={ent} onValueChange={setEnt}>
              <SelectTrigger className="w-48 bg-white" data-testid="payroll-entity-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All entities</SelectItem>{ents.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-white" data-testid="payroll-month-input" />
          <Button onClick={run} disabled={busy} data-testid="run-payroll-button" className="bg-[#E08A1E] hover:bg-[#C87515] text-white rounded-full"><Play className="h-4 w-4 mr-1" />{busy ? "Processing..." : "Run payroll"}</Button>
        </>} />
      {isAdmin && list.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Employees paid" value={list.length} testid="payroll-count" />
          <StatCard label="Total gross" value={inr(sum("gross"))} testid="payroll-gross" />
          <StatCard label="Total PF" value={inr(sum("pf"))} testid="payroll-pf" />
          <StatCard label="Total net pay" value={inr(sum("net_pay"))} accent testid="payroll-net" />
        </div>
      )}
      {!list.length ? <EmptyState text={isAdmin ? `No payroll processed for ${monthLabel(month)}. Employees need an annual CTC to be included.` : "No payslips yet"} testid="payroll-empty" /> : (
        <div className="bg-white border border-[#E5E5EA] rounded-2xl overflow-x-auto">
          <Table data-testid="payroll-table">
            <TableHeader><TableRow>{isAdmin ? <TableHead>Employee</TableHead> : <TableHead>Month</TableHead>}<TableHead>Paid days</TableHead><TableHead>Gross</TableHead><TableHead>Deductions</TableHead><TableHead>Net pay</TableHead><TableHead className="text-right">Payslip</TableHead></TableRow></TableHeader>
            <TableBody>{list.map((p) => (
              <TableRow key={p.id} data-testid={`payslip-row-${p.employee_code}-${p.month}`}>
                <TableCell>{isAdmin ? <><div className="font-semibold">{p.employee_name}</div><div className="text-xs text-[#6E6E73]">{p.employee_code}</div></> : monthLabel(p.month)}</TableCell>
                <TableCell>{p.paid_days}/{p.days_in_month}</TableCell>
                <TableCell>{inr(p.gross)}</TableCell><TableCell>{inr(p.total_deductions)}</TableCell>
                <TableCell className="font-bold">{inr(p.net_pay)}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => setSlip(p)} data-testid={`view-payslip-${p.employee_code}-${p.month}`}><Eye className="h-4 w-4 mr-1" />View</Button></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </div>
      )}
      <PayslipDialog slip={slip} onClose={() => setSlip(null)} />
    </div>
  );
}
